import { type GetTagGroupResponse } from '@hungryroot/rest';
import { Goals, HouseholdSize, type Quiz } from '../defs/quiz';

export const SEGMENT_QUIZ_RESULTS_EVENT_NAME = 'Quiz Results';

const goalsSegmentProperties = {
  [Goals.SaveTime]: 'save_time',
  [Goals.ImproveHealth]: 'improve_health',
  [Goals.LoseWeight]: 'lose_weight',
  [Goals.TryNewThings]: 'try_new_things',
  [Goals.GroceryShopLess]: 'grocery_shop_less',
  [Goals.WasteLessFood]: 'waste_less_food',
  [Goals.SaveMoney]: 'save_money',
  [Goals.LowerStress]: 'lower_stress',
  [Goals.SimplifyCooking]: 'simplify_cooking',
  [Goals.EatLessTakeout]: 'eat_less_takeout',
};

function getAdultSegmentValue(
  quiz: Pick<Quiz, 'householdSize' | 'adultsCount'>
) {
  const { householdSize, adultsCount } = quiz;

  if (adultsCount !== null) {
    return adultsCount;
  }

  if (
    householdSize === HouseholdSize.ME_PLUS_MORE ||
    householdSize === HouseholdSize.ME_PLUS_ONE
  ) {
    // We display "1" to the user in this scenario, so we want to log it
    return 1;
  }

  return adultsCount;
}

export function getGoalsSegmentData(goals: Goals[]) {
  return Object.keys(goalsSegmentProperties).reduce(
    (acc, key) => {
      const k = parseInt(key);
      const segmentKey =
        goalsSegmentProperties[k as keyof typeof goalsSegmentProperties];

      acc[segmentKey] = goals.includes(k);
      return acc;
    },
    {} as { [key: string]: boolean | undefined }
  );
}

/**
 * The `Quiz Results` payload slice for the budget question, or `undefined`
 * when the selected tag can't be named.
 *
 * Both quiz flows fire this: the full funnel from `Quiz.tsx`'s pathname
 * switch, the mini quiz from `BudgetQuestion` (its tail route never matches
 * that switch). Sharing the builder is what keeps the two arms comparable in
 * the warehouse.
 *
 * Returning `undefined` rather than tracking a null name is deliberate — a
 * `{ budget: null }` row would be a value the control arm never emits.
 */
export function getBudgetSegmentData(
  budget: Quiz['budget'],
  budgetTags: GetTagGroupResponse | undefined
): { budget: string } | undefined {
  if (budget === null || budget === undefined || budgetTags === undefined) {
    return undefined;
  }

  const name = budgetTags.tags?.[0]?.find((tag) => tag.id === budget)?.name;

  // Truthy rather than a null check, so a blank name is dropped instead of
  // emitting `{ budget: '' }`. Mirrors the `if (tag?.name)` guard this
  // replaced in Quiz.tsx, keeping full-funnel behavior identical.
  return name ? { budget: name } : undefined;
}

export function getHouseholdSizeSegmentData(quiz: Quiz) {
  return {
    household_size: quiz.householdSize,
    num_adults: getAdultSegmentValue(quiz),
    num_older_children: quiz.childrenCount,
    num_younger_children: quiz.youngChildrenCount,

    // deprecated: but not removing so we don't break anything in Segment
    num_middle_children: 0,
  };
}
